import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Loader2, Lock, Eye, EyeOff, Check, X } from "lucide-react";

import userServices from "../../services/userServices";

/* =========================================================================
 *  Settings — account/security for the signed-in user (/settings).
 *  Change password (PUT /user/password).
 *
 *  The new-password rules mirror the backend exactly (AuthRequest →
 *  Password::min(6)->letters()->mixedCase(), confirmed) and are shown as a
 *  live checklist so the user can't submit something the server will reject.
 * ========================================================================= */

const PASSWORD_RULES = [
    { key: "len", label: "At least 6 characters", test: (p) => p.length >= 6 },
    { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
    { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
];

export default function Settings() {
    const navigate = useNavigate();
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

    const [form, setForm] = useState({ current_password: "", password: "", password_confirmation: "" });
    const [show, setShow] = useState({ current_password: false, password: false, password_confirmation: false });
    const [saving, setSaving] = useState(false);

    if (!isLoggedIn) {
        navigate("/");
        return null;
    }

    const onField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const toggle = (key) => setShow((s) => ({ ...s, [key]: !s[key] }));

    // Live rule evaluation against the new password.
    const ruleResults = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(form.password) }));
    const allRulesPass = ruleResults.every((r) => r.ok);
    const matches = form.password.length > 0 && form.password === form.password_confirmation;
    const newDiffersFromCurrent =
        form.password.length === 0 || form.password !== form.current_password;
    const canSubmit =
        form.current_password.length > 0 && allRulesPass && matches && newDiffersFromCurrent && !saving;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.current_password) { toast.error("Enter your current password."); return; }
        if (!allRulesPass) { toast.error("New password doesn't meet all the requirements."); return; }
        if (!newDiffersFromCurrent) { toast.error("New password must be different from the current one."); return; }
        if (!matches) { toast.error("New password and confirmation don't match."); return; }

        setSaving(true);
        try {
            const res = await userServices.changePassword(form);
            toast.success(res?.data?.message || "Password updated.");
            setForm({ current_password: "", password: "", password_confirmation: "" });
        } catch (err) {
            const msg =
                err?.response?.data?.errors
                    ? Object.values(err.response.data.errors)[0]?.[0]
                    : err?.response?.data?.message || err?.response?.data?.error;
            toast.error(msg || "Could not update password.");
        } finally {
            setSaving(false);
        }
    };

    const inputClass =
        "w-full bg-white border border-gray-200 rounded-xl py-3 pl-4 pr-11 text-[15px] font-medium text-gray-900 outline-none focus:border-gray-900 transition-colors";
    const labelClass = "block text-sm font-bold text-gray-700 mb-1.5";

    // Plain function (not a component) so the inputs keep focus across renders.
    const passwordField = (label, key, autoComplete) => (
        <div>
            <label className={labelClass}>{label}</label>
            <div className="relative">
                <input
                    type={show[key] ? "text" : "password"}
                    value={form[key]}
                    onChange={onField(key)}
                    className={inputClass}
                    autoComplete={autoComplete}
                />
                <button
                    type="button"
                    onClick={() => toggle(key)}
                    aria-label={show[key] ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-gray-400 hover:text-gray-700 bg-transparent border-none p-0"
                >
                    {show[key] ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 py-8 md:py-12 animate-fade-in">
            <div className="max-w-2xl mx-auto px-4 sm:px-6">
                <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-1">Settings</h1>
                <p className="text-gray-500 font-medium mb-8">Manage your account security.</p>

                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
                    <h3 className="flex items-center gap-2 text-lg font-black text-gray-900">
                        <Lock size={18} /> Change password
                    </h3>

                    {passwordField("Current password", "current_password", "current-password")}
                    {passwordField("New password", "password", "new-password")}

                    {/* Live requirements checklist (mirrors the backend rules). */}
                    <ul className="space-y-1.5 -mt-1">
                        {ruleResults.map((r) => (
                            <li
                                key={r.key}
                                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                                    r.ok ? "text-green-600" : "text-gray-400"
                                }`}
                            >
                                {r.ok ? <Check size={15} className="shrink-0" /> : <X size={15} className="shrink-0" />}
                                {r.label}
                            </li>
                        ))}
                        {form.password.length > 0 && (
                            <li
                                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                                    newDiffersFromCurrent ? "text-green-600" : "text-red-500"
                                }`}
                            >
                                {newDiffersFromCurrent ? <Check size={15} className="shrink-0" /> : <X size={15} className="shrink-0" />}
                                Different from current password
                            </li>
                        )}
                    </ul>

                    {passwordField("Confirm new password", "password_confirmation", "new-password")}

                    {/* Match indicator (only once the user starts confirming). */}
                    {form.password_confirmation.length > 0 && (
                        <p
                            className={`flex items-center gap-2 text-sm font-medium -mt-1 ${
                                matches ? "text-green-600" : "text-red-500"
                            }`}
                        >
                            {matches ? <Check size={15} className="shrink-0" /> : <X size={15} className="shrink-0" />}
                            {matches ? "Passwords match" : "Passwords don't match"}
                        </p>
                    )}

                    <div className="pt-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={!canSubmit}
                            className="inline-flex items-center gap-2 bg-[#ffea00] hover:bg-[#e6d300] text-black font-black px-7 py-3 rounded-xl border-none shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving && <Loader2 size={16} className="animate-spin" />}
                            {saving ? "Updating…" : "Update password"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
